import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip,
    Checkbox,
    Divider,
    InputBase,
    useMediaQuery,
    useTheme,
    Grid,
    Snackbar,
    Collapse,
} from '@mui/material';
import {
    Add,
    MoreVert,
    Tune,
    ExpandMore,
    ExpandLess,
    Edit,
    Delete,
    Visibility,
    Public,
    Lock,
    CalendarToday,
    LocationOn,
    Flight,
    ArrowBack,
    People,
    Person,
    Group,
    Explore,
    Place,
    Print,
    Download,
    TableChart,
    ArrowUpward,
    ArrowDownward,
    Search,
    ShoppingCartCheckout,
    Info,
    CloudUpload,
} from '@mui/icons-material';
import { api, isAuthenticated } from '../utils/api';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import ComingSoonBadge from '../components/common/ComingSoonBadge';
import { SidebarAd, InFeedAd } from '../components/ads';
import TripImportCard from '../components/trips/TripImportCard';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { formatDateRange, parseLocalDate, todayLocalString } from '../utils/dateHelpers';

// Types
interface Trip {
    tripId: string;
    title: string;
    destination: string;
    startDate: string;
    endDate: string;
    visibility: string;
    status: 'planning' | 'upcoming' | 'ongoing' | 'completed' | 'booking' | 'booked' | 'cancelled' | 'deleted';
    coverImage?: string;
    description?: string;
    createdAt: string;
    isGroup?: boolean;
    isDiscovery?: boolean;
    isDestination?: boolean;
    reservationCode?: string;
    importSource?: string;
    tripSource?: 'percoro' | 'community' | 'destination' | 'self_imported';
    bookings?: { flights?: any[]; hotels?: any[]; cars?: any[]; activities?: any[] };
}

interface PendingInvitation {
    tripId: string;
    title: string;
    destination: string;
    startDate: string;
    endDate: string;
    status: string;
    companionId: string;
    invitedAt: string;
    companionType: 'shared' | 'travel_companion';
    ownerName: string;
}

type TripTypeFilter = 'all' | 'individual' | 'group';
type TripSourceFilter = 'all' | 'percoro' | 'community' | 'destination' | 'self_imported';
type SortColumn = 'dates' | 'title' | 'destination' | 'type' | 'status' | 'tripId' | 'reservationCode';
type SortDirection = 'asc' | 'desc';

// Styles
const typeStyles = {
    individual: { border: '#2D6E6A', label: 'Individual', icon: Person },
    group: { border: '#1976D2', label: 'Group', icon: Group },
};

const sourceStyles = {
    percoro: { bg: '#E0F2F1', color: '#2D6E6A', border: 'rgba(0,180,160,0.25)', label: 'Percoro', icon: TravelExploreIcon },
    community: { bg: '#fff3e0', color: '#e65100', border: 'rgba(245,124,0,0.25)', label: 'Community', icon: Explore },
    destination: { bg: '#f3e5f5', color: '#3D5A8A', border: 'rgba(156,39,176,0.25)', label: 'Destination', icon: Place },
    self_imported: { bg: '#FCE4EC', color: '#C2185B', border: 'rgba(194,24,91,0.25)', label: 'Self Imported', icon: CloudUpload },
};

// Helpers

const getStatus = (t: Trip): { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'info' | 'error'; priority: number } => {
    // Cancelled and Deleted statuses
    if (t.status === 'cancelled') return { label: 'Cancelled', color: 'error', priority: 5 };
    if (t.status === 'deleted') return { label: 'Deleted', color: 'default', priority: 6 };
    
    // Imported trips with confirmation code = Booked
    const hasConfirmation = t.reservationCode || 
        t.bookings?.flights?.some(f => f.confirmationCode) ||
        t.bookings?.hotels?.some(h => h.confirmationCode) ||
        t.bookings?.cars?.some(c => c.confirmationCode) ||
        t.bookings?.activities?.some(a => a.confirmationCode);
    
    if ((t.importSource === 'pdf_import' || t.tripSource === 'self_imported') && hasConfirmation) {
        return { label: 'Booked', color: 'success', priority: 4 };
    }
    
    if (t.status === 'booked' || t.status === 'booking') return { label: 'Booked', color: 'success', priority: 4 };
    if (!t.startDate) return { label: 'Planning', color: 'default', priority: 2 };
    const now = new Date(), start = parseLocalDate(t.startDate)!, end = t.endDate ? parseLocalDate(t.endDate)! : start;
    if (now >= start && now <= end) return { label: 'Ongoing', color: 'success', priority: 0 };
    if (now < start) {
        const d = Math.ceil((start.getTime() - now.getTime()) / 86400000);
        return d <= 7 ? { label: `${d}d away`, color: 'warning', priority: 1 } : { label: 'Upcoming', color: 'info', priority: 1 };
    }
    return { label: 'Completed', color: 'default', priority: 3 };
};

const getVisInfo = (v: string) => {
    if (v === 'public') return { icon: <Public sx={{ fontSize: 13 }} />, label: 'Public' };
    if (v === 'bffs_travel_partners') return { icon: <People sx={{ fontSize: 13 }} />, label: 'BFFs/Travel Partners' };
    if (v === 'friends_family') return { icon: <People sx={{ fontSize: 13 }} />, label: 'Friends & Family' };
    return { icon: <Lock sx={{ fontSize: 13 }} />, label: 'Private' };
};

const sortVal = (t: Trip): number => {
    const s = getStatus(t), now = Date.now(), start = t.startDate ? (parseLocalDate(t.startDate)?.getTime() ?? Infinity) : Infinity;
    switch (s.priority) {
        case 0: return -2e9 + ((t.endDate ? (parseLocalDate(t.endDate)?.getTime() ?? start) : start) - now);
        case 1: return -1e9 + (start - now);
        case 2: return -new Date(t.createdAt).getTime() + 1e9;
        case 3: return 2e9 - (t.endDate ? (parseLocalDate(t.endDate)?.getTime() ?? 0) : 0);
        default: return 0;
    }
};

const doExportCSV = (trips: Trip[]) => {
    const h = ['Trip ID', 'Reservation Code', 'Trip Name', 'Type', 'Source', 'Destination', 'Start Date', 'End Date', 'Status', 'Visibility', 'Created'];
    const r = trips.map(t => {
        const src = [t.isDiscovery ? 'Discovery' : '', t.isDestination ? 'Destination' : ''].filter(Boolean).join(', ') || '';
        return [t.tripId, t.reservationCode || '', `"${t.title}"`, t.isGroup ? 'Group' : 'Individual', src,
            `"${t.destination}"`, t.startDate || '', t.endDate || '', getStatus(t).label, t.visibility, t.createdAt?.split('T')[0] || ''].join(',');
    });
    const blob = new Blob([[h.join(','), ...r].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `percoro-trips-${todayLocalString()}.csv`;
    a.click();
};
const doExportPrint = (trips: Trip[]) => {
    const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>My Trips - Percoro</title>
            <style>
                body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; }
                .logo { color: #2D6E6A; font-weight: 700; font-size: 24px; margin-bottom: 24px; }
                h1 { color: #333; font-size: 22px; margin-bottom: 20px; }
                .trip { border: 1px solid #eee; border-radius: 8px; padding: 16px; margin-bottom: 12px; border-left: 4px solid #2D6E6A; }
                .trip-title { font-weight: 600; font-size: 16px; margin-bottom: 4px; }
                .trip-dest { color: #666; font-size: 14px; }
                .trip-dates { color: #888; font-size: 13px; margin-top: 4px; }
                .trip-status { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; background: #E8F5E9; color: #2E7D32; margin-left: 8px; }
                .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #eee; color: #999; font-size: 11px; text-align: center; }
                @media print { body { padding: 20px; } }
            </style>
        </head>
        <body>
            <div class="logo">PERCORO</div>
            <h1>My Trips (${trips.length})</h1>
            ${trips.map(t => `
                <div class="trip">
                    <div class="trip-title">${t.title}<span class="trip-status">${getStatus(t).label}</span></div>
                    <div class="trip-dest">${t.destination}</div>
                    <div class="trip-dates">${formatDateRange(t.startDate, t.endDate, { month: 'short', day: 'numeric' })}${t.reservationCode ? ` • Res: ${t.reservationCode}` : ''}</div>
                </div>
            `).join('')}
            <div class="footer">Generated from Percoro · ${new Date().toLocaleDateString()}</div>
        </body>
        </html>
    `;
    
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow?.document;
    if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
        
        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 300);
    }
};

const doExportPDF = (trips: Trip[]) => {
    const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>My Trips - Percoro</title>
            <style>
                body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; }
                .logo { color: #2D6E6A; font-weight: 700; font-size: 24px; margin-bottom: 24px; }
                h1 { color: #333; font-size: 22px; margin-bottom: 20px; }
                .instructions { background: #FFF9E6; border: 1px solid #FFE082; border-radius: 8px; padding: 12px; margin-bottom: 24px; font-size: 13px; color: #666; }
                .trip { border: 1px solid #eee; border-radius: 8px; padding: 16px; margin-bottom: 12px; border-left: 4px solid #2D6E6A; }
                .trip-title { font-weight: 600; font-size: 16px; margin-bottom: 4px; }
                .trip-dest { color: #666; font-size: 14px; }
                .trip-dates { color: #888; font-size: 13px; margin-top: 4px; }
                .trip-status { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; background: #E8F5E9; color: #2E7D32; margin-left: 8px; }
                .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #eee; color: #999; font-size: 11px; text-align: center; }
                @media print { .instructions { display: none; } body { padding: 20px; } }
            </style>
        </head>
        <body>
            <div class="logo">PERCORO</div>
            <div class="instructions"><strong>To save as PDF:</strong> Press Ctrl+P (or Cmd+P on Mac), then select "Save as PDF" as the destination.</div>
            <h1>My Trips (${trips.length})</h1>
            ${trips.map(t => `
                <div class="trip">
                    <div class="trip-title">${t.title}<span class="trip-status">${getStatus(t).label}</span></div>
                    <div class="trip-dest">${t.destination}</div>
                    <div class="trip-dates">${formatDateRange(t.startDate, t.endDate, { month: 'short', day: 'numeric' })}${t.reservationCode ? ` • Res: ${t.reservationCode}` : ''}</div>
                </div>
            `).join('')}
            <div class="footer">Generated from Percoro · ${new Date().toLocaleDateString()}</div>
        </body>
        </html>
    `;
    
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow?.document;
    if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
        
        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 300);
    }
};
// Component
const MyTripsPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // View state persistence (sort/filter selections survive across sessions)
    const VIEW_STATE_KEY = 'myTrips.viewState';
    const _vs = (() => {
        try {
            const raw = localStorage.getItem(VIEW_STATE_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    })();

    const [trips, setTrips] = useState<Trip[]>([]);
    const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [tabValue, setTabValue] = useState<number>(typeof _vs.tabValue === 'number' ? _vs.tabValue : 0);
    const [tripTypeFilter, setTripTypeFilter] = useState<TripTypeFilter>(_vs.tripTypeFilter ?? 'all');
    const [tripSourceFilter, setTripSourceFilter] = useState<TripSourceFilter>(_vs.tripSourceFilter ?? 'all');
    const [sharingFilter, setSharingFilter] = useState<'all' | 'shared' | 'companions' | 'view_only'>('all');
    const [sortColumn, setSortColumn] = useState<SortColumn>(_vs.sortColumn ?? 'dates');
    const [sortDirection, setSortDirection] = useState<SortDirection>(_vs.sortDirection ?? 'asc');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [bookingPromptOpen, setBookingPromptOpen] = useState(false);
    const [bookingTrip, setBookingTrip] = useState<Trip | null>(null);
    const [comingSoonMessage, setComingSoonMessage] = useState('');
    const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
    const bookingEnabled = !flagsLoading && isEnabled('booking_enabled') === true;
    const bookingMyTrips = !flagsLoading && isEnabled('booking_my_trips') === true;
    const showBookOnMyTrips = bookingEnabled && bookingMyTrips;
    const groupTripsEnabled = isEnabled('group_trips');

    // View menu section expansion state (Export expanded by default)
    const [expanded, setExpanded] = useState({
        sort: false,
        status: false,
        type: false,
        source: false,
        exportSection: true,
    });
    const toggleSection = (section: keyof typeof expanded) =>
        setExpanded(p => ({ ...p, [section]: !p[section] }));

    // NEW: Import card state
    const [showImportCard, setShowImportCard] = useState(false);

    const fetchTrips = useCallback(async () => {
        try {
            if (!isAuthenticated()) { navigate('/login'); return; }
            const res = await api.get('/trips');
            if (!res.ok) throw new Error('Failed');
            const data = await res.json();
            setTrips(data.trips || []);
            setPendingInvitations(data.pendingInvitations || []);
        } catch (err) {
            console.error(err);
            setError('Failed to load your trips');
        } finally { setLoading(false); }
    }, [navigate]);

    const [respondingId, setRespondingId] = useState<string | null>(null);
    const [inviteSnackbar, setInviteSnackbar] = useState<string | null>(null);

    const respondToInvitation = async (tripId: string, companionId: string, action: 'accept' | 'decline') => {
        setRespondingId(companionId);
        try {
            const res = await api.post(`/trips/${tripId}/companions/${companionId}`, { action });
            if (!res.ok) throw new Error('Failed');
            setInviteSnackbar(action === 'accept' ? 'Trip added to your trips!' : 'Invitation declined.');
            await fetchTrips();
        } catch {
            setError('Failed to respond to invitation. Please try again.');
        } finally {
            setRespondingId(null);
        }
    };

    useEffect(() => { fetchTrips(); }, [fetchTrips]);

    // View options menu anchor (the new ⋯ menu, separate from row-action anchorEl)
    const [viewMenuAnchorEl, setViewMenuAnchorEl] = useState<null | HTMLElement>(null);

    // Persist view state (sort/filter selections) across sessions
    useEffect(() => {
        try {
            localStorage.setItem(VIEW_STATE_KEY, JSON.stringify({
                tabValue, tripTypeFilter, tripSourceFilter, sortColumn, sortDirection
            }));
        } catch { /* localStorage may be unavailable in private mode */ }
    }, [tabValue, tripTypeFilter, tripSourceFilter, sortColumn, sortDirection]);

    // Filtering
    const filterByType = useCallback((l: Trip[]) => {
        if (tripTypeFilter === 'individual') return l.filter(t => !t.isGroup);
        if (tripTypeFilter === 'group') return l.filter(t => t.isGroup === true);
        return l;
    }, [tripTypeFilter]);

    const filterBySource = useCallback((l: Trip[]) => {
        if (tripSourceFilter === 'percoro') return l.filter(t => t.tripSource === 'percoro' || (!t.tripSource && !t.isDiscovery && !t.isDestination && t.importSource !== 'pdf_import'));
        if (tripSourceFilter === 'community') return l.filter(t => t.tripSource === 'community' || t.isDiscovery === true);
        if (tripSourceFilter === 'destination') return l.filter(t => t.tripSource === 'destination' || t.isDestination === true);
        if (tripSourceFilter === 'self_imported') return l.filter(t => t.tripSource === 'self_imported' || t.importSource === 'pdf_import');
        return l;
    }, [tripSourceFilter]);

    const filterByStatus = useCallback((tab: number, l: Trip[]) => {
        const now = new Date();
        // Tabs 0-3 exclude cancelled and deleted
        const activeTrips = l.filter(t => t.status !== 'cancelled' && t.status !== 'deleted');
        
        if (tab === 0) return activeTrips; // All (excludes cancelled/deleted)
        if (tab === 1) return activeTrips.filter(t => { if (!t.startDate) return true; const s = parseLocalDate(t.startDate); return s ? s > now : true; }); // Upcoming
        if (tab === 2) return activeTrips.filter(t => {
            if (!t.startDate) return false;
            const s = parseLocalDate(t.startDate)!, e = t.endDate ? parseLocalDate(t.endDate)! : s;
            return now >= s && now <= e;
        }); // Ongoing
        if (tab === 3) return activeTrips.filter(t => { if (!t.endDate) return false; const e = parseLocalDate(t.endDate); return e ? e < now : false; }); // Completed
        if (tab === 4) return l.filter(t => t.status === 'cancelled'); // Cancelled
        if (tab === 5) return l.filter(t => t.status === 'deleted'); // Deleted
        return activeTrips;
    }, []);

    const filterBySearch = useCallback((l: Trip[]) => {
        if (!searchQuery.trim()) return l;
        const q = searchQuery.toLowerCase().trim();
        return l.filter(t =>
            t.title.toLowerCase().includes(q) ||
            t.destination.toLowerCase().includes(q) ||
            t.tripId.toLowerCase().includes(q) ||
            (t.reservationCode && t.reservationCode.toLowerCase().includes(q))
        );
    }, [searchQuery]);

    const sortTrips = useCallback((l: Trip[]) => {
        return [...l].sort((a, b) => {
            let c = 0;
            switch (sortColumn) {
                case 'title': c = a.title.localeCompare(b.title); break;
                case 'type': c = (a.isGroup ? 1 : 0) - (b.isGroup ? 1 : 0); break;
                case 'destination': c = (a.destination || '').localeCompare(b.destination || ''); break;
                case 'dates': c = sortVal(a) - sortVal(b); break;
                case 'status': c = getStatus(a).priority - getStatus(b).priority; break;
                case 'tripId': c = a.tripId.localeCompare(b.tripId); break;
                case 'reservationCode': c = (a.reservationCode || '').localeCompare(b.reservationCode || ''); break;
            }
            return sortDirection === 'desc' ? -c : c;
        });
    }, [sortColumn, sortDirection]);

    const handleSort = (col: SortColumn) => {
        if (sortColumn === col) setSortDirection(p => p === 'asc' ? 'desc' : 'asc');
        else { setSortColumn(col); setSortDirection('asc'); }
    };

    const base = useMemo(() => filterBySearch(filterBySource(filterByType(trips))), [trips, filterByType, filterBySource, filterBySearch]);
    const getTabCount = useCallback((t: number) => {
        if (t === 4) return trips.filter(trip => trip.status === 'cancelled').length;
        if (t === 5) return trips.filter(trip => trip.status === 'deleted').length;
        return filterByStatus(t, base).length;
    }, [base, filterByStatus, trips]);
    const filteredTrips = useMemo(() => sortTrips(filterByStatus(tabValue, base)), [base, tabValue, filterByStatus, sortTrips]);

    // Selection
    const toggleRow = (id: string) => setSelectedRows(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

    // Actions
    const menuOpen = (e: React.MouseEvent<HTMLElement>, trip: Trip) => { e.stopPropagation(); setAnchorEl(e.currentTarget); setSelectedTrip(trip); };
    const menuClose = () => setAnchorEl(null);
    const viewTrip = () => { if (selectedTrip) navigate(`/trips/${selectedTrip.tripId}`); menuClose(); };
    const editTrip = () => { if (selectedTrip) navigate(`/trips/${selectedTrip.tripId}`); menuClose(); };
    const printTrip = () => {
        menuClose();
        if (!selectedTrip) return;
        
        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${selectedTrip.title} - Percoro</title>
                <style>
                    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                    .logo { color: #2D6E6A; font-weight: 700; font-size: 24px; margin-bottom: 24px; }
                    h1 { color: #333; font-size: 22px; margin-bottom: 8px; }
                    .destination { color: #666; font-size: 16px; margin-bottom: 16px; }
                    .meta { display: flex; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
                    .meta-item { font-size: 13px; color: #666; }
                    .meta-label { font-weight: 600; color: #333; }
                    .status { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; background: #E8F5E9; color: #2E7D32; }
                    .description { margin-top: 20px; padding: 16px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #2D6E6A; }
                    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; color: #999; font-size: 11px; text-align: center; }
                    @media print { body { padding: 20px; } }
                </style>
            </head>
            <body>
                <div class="logo">PERCORO</div>
                <h1>${selectedTrip.title}</h1>
                <div class="destination">📍 ${selectedTrip.destination}</div>
                <div class="meta">
                    <div class="meta-item"><span class="meta-label">Dates:</span> ${formatDateRange(selectedTrip.startDate, selectedTrip.endDate, { month: 'short', day: 'numeric' })}</div>
                    <div class="meta-item"><span class="meta-label">Type:</span> ${selectedTrip.isGroup ? 'Group' : 'Individual'}</div>
                    <div class="meta-item"><span class="meta-label">Status:</span> <span class="status">${getStatus(selectedTrip).label}</span></div>
                    ${selectedTrip.reservationCode ? `<div class="meta-item"><span class="meta-label">Reservation:</span> ${selectedTrip.reservationCode}</div>` : ''}
                </div>
                ${selectedTrip.description ? `<div class="description">${selectedTrip.description}</div>` : ''}
                <div class="footer">Generated from Percoro · ${new Date().toLocaleDateString()}</div>
            </body>
            </html>
        `;
        
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow?.document;
        if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(html);
            iframeDoc.close();
            
            setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 1000);
            }, 300);
        }
    };
    
    const downloadPDF = () => {
        menuClose();
        if (!selectedTrip) return;
        
        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${selectedTrip.title} - Percoro</title>
                <style>
                    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                    .logo { color: #2D6E6A; font-weight: 700; font-size: 24px; margin-bottom: 24px; }
                    .instructions { background: #FFF9E6; border: 1px solid #FFE082; border-radius: 8px; padding: 12px; margin-bottom: 24px; font-size: 13px; color: #666; }
                    h1 { color: #333; font-size: 22px; margin-bottom: 8px; }
                    .destination { color: #666; font-size: 16px; margin-bottom: 16px; }
                    .meta { display: flex; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
                    .meta-item { font-size: 13px; color: #666; }
                    .meta-label { font-weight: 600; color: #333; }
                    .status { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; background: #E8F5E9; color: #2E7D32; }
                    .description { margin-top: 20px; padding: 16px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #2D6E6A; }
                    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; color: #999; font-size: 11px; text-align: center; }
                    @media print { .instructions { display: none; } body { padding: 20px; } }
                </style>
            </head>
            <body>
                <div class="logo">PERCORO</div>
                <div class="instructions"><strong>To save as PDF:</strong> Press Ctrl+P (or Cmd+P on Mac), then select "Save as PDF" as the destination.</div>
                <h1>${selectedTrip.title}</h1>
                <div class="destination">📍 ${selectedTrip.destination}</div>
                <div class="meta">
                    <div class="meta-item"><span class="meta-label">Dates:</span> ${formatDateRange(selectedTrip.startDate, selectedTrip.endDate, { month: 'short', day: 'numeric' })}</div>
                    <div class="meta-item"><span class="meta-label">Type:</span> ${selectedTrip.isGroup ? 'Group' : 'Individual'}</div>
                    <div class="meta-item"><span class="meta-label">Status:</span> <span class="status">${getStatus(selectedTrip).label}</span></div>
                    ${selectedTrip.reservationCode ? `<div class="meta-item"><span class="meta-label">Reservation:</span> ${selectedTrip.reservationCode}</div>` : ''}
                </div>
                ${selectedTrip.description ? `<div class="description">${selectedTrip.description}</div>` : ''}
                <div class="footer">Generated from Percoro · ${new Date().toLocaleDateString()}</div>
            </body>
            </html>
        `;
        
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow?.document;
        if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(html);
            iframeDoc.close();
            
            setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 1000);
            }, 300);
        }
    };
    const deleteClick = () => { setDeleteDialogOpen(true); menuClose(); };
    const bookFromMenu = () => {
        if (selectedTrip) { setBookingTrip(selectedTrip); setBookingPromptOpen(true); }
        menuClose();
    };

    const handleBookClick = (e: React.MouseEvent, trip: Trip) => {
        e.stopPropagation();
        if (!bookingEnabled) {
            setComingSoonMessage('Booking is coming soon - stay tuned!');
            return;
        }
        setBookingTrip(trip);
        setBookingPromptOpen(true);
    };

    const handleQuickBook = () => {
        if (bookingTrip) {
            navigate(`/trips/${bookingTrip.tripId}/book`);
        }
        setBookingPromptOpen(false);
    };

    const handleReviewThenBook = () => {
        if (bookingTrip) {
            navigate(`/trips/${bookingTrip.tripId}`);
        }
        setBookingPromptOpen(false);
    };

    const deleteConfirm = async () => {
        if (!selectedTrip) return;
        setDeleting(true);
        try {
            const res = await api.delete(`/trips/${selectedTrip.tripId}`);
            if (!res.ok) throw new Error('Failed');
            setTrips(trips.filter(t => t.tripId !== selectedTrip.tripId));
            setSelectedRows(p => { const n = new Set(p); n.delete(selectedTrip.tripId); return n; });
            setDeleteDialogOpen(false); setSelectedTrip(null);
        } catch (err) {
            console.error(err);
            setError('Failed to delete trip');
        } finally { setDeleting(false); }
    };

    // NEW: Handle import complete
    const handleImportComplete = (tripId: string) => {
        setShowImportCard(false);
        fetchTrips(); // Refresh the trips list
        // Optionally navigate to the new trip
        // navigate(`/trips/${tripId}`);
    };

    // Sub-components
    // Render
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: '#2D6E6A' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 3 }, pt: { xs: 8, sm: 3 } }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={9}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                        <IconButton onClick={() => navigate('/explore')} sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h4" sx={{
                            fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', flexGrow: 1,
                        }}>
                            My Trips
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<ShoppingCartCheckout />}
                            onClick={() => bookingEnabled ? setComingSoonMessage('Select a trip to book') : undefined}
                            disabled={!bookingEnabled}
                            sx={{
                                borderColor: '#FF6B35',
                                color: '#FF6B35',
                                borderRadius: '24px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                                '&:hover': { borderColor: '#E55A2B', bgcolor: '#FFF8F5' },
                                '&.Mui-disabled': { borderColor: '#FFB89A', color: '#FFB89A' },
                            }}
                        >
                            Book
                            {!bookingEnabled && <ComingSoonBadge />}
                        </Button>
                        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/trips/new')}
                            sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#009688' } }}>
                            Create Trip
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Tune />}
                            onClick={(e) => setViewMenuAnchorEl(e.currentTarget)}
                            aria-label="Filter and export options"
                            sx={{
                                borderColor: '#e0e0e0',
                                color: '#555',
                                borderRadius: '24px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                '&:hover': { borderColor: '#2D6E6A', color: '#2D6E6A', bgcolor: '#2D6E6A08' },
                            }}
                        >
                            Filter / Export
                        </Button>
                    </Box>

                    {/* View Options Menu (Rule 1: secondary controls live here) */}
                    <Menu
                        anchorEl={viewMenuAnchorEl}
                        open={Boolean(viewMenuAnchorEl)}
                        onClose={() => setViewMenuAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{ sx: { minWidth: 240, maxHeight: '70vh', borderRadius: 2 } }}
                    >
                        {/* SECTION: Sort */}
                        <MenuItem onClick={() => toggleSection('sort')} sx={{ px: 2, py: 0.75, '&:hover': { bgcolor: '#f5f5f5' } }}>
                            <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Sort by</Typography>
                            {expanded.sort ? <ExpandLess sx={{ fontSize: 18, color: '#888' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#888' }} />}
                        </MenuItem>
                        <Collapse in={expanded.sort} timeout="auto" unmountOnExit>
                        {([
                            ['dates', 'Dates'],
                            ['title', 'Name'],
                            ['destination', 'Destination'],
                            ['type', 'Type'],
                            ['status', 'Status'],
                            ['tripId', 'Trip ID'],
                            ['reservationCode', 'Reservation'],
                        ] as [SortColumn, string][]).map(([col, label]) => {
                            const isActive = sortColumn === col;
                            return (
                                <MenuItem key={col} onClick={() => { handleSort(col); }} sx={{ fontSize: '0.85rem', py: 0.75 }}>
                                    <Box sx={{ flex: 1 }}>{label}</Box>
                                    {isActive && (sortDirection === 'asc' ? <ArrowUpward sx={{ fontSize: 16, color: '#2D6E6A', ml: 1 }} /> : <ArrowDownward sx={{ fontSize: 16, color: '#2D6E6A', ml: 1 }} />)}
                                </MenuItem>
                            );
                        })}
                        </Collapse>
                        <Divider sx={{ my: 0.5 }} />

                        {/* SECTION: Filter by status */}
                        <MenuItem onClick={() => toggleSection('status')} sx={{ px: 2, py: 0.75, '&:hover': { bgcolor: '#f5f5f5' } }}>
                            <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by status</Typography>
                            {expanded.status ? <ExpandLess sx={{ fontSize: 18, color: '#888' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#888' }} />}
                        </MenuItem>
                        <Collapse in={expanded.status} timeout="auto" unmountOnExit>
                        {([
                            [0, 'All'],
                            [1, 'Upcoming'],
                            [2, 'Ongoing'],
                            [3, 'Completed'],
                        ] as [number, string][]).map(([val, label]) => {
                            const isActive = tabValue === val;
                            const count = getTabCount(val);
                            return (
                                <MenuItem key={val} onClick={() => { setTabValue(val); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75, fontWeight: isActive ? 600 : 400, color: isActive ? '#2D6E6A' : 'inherit' }}>
                                    <Box sx={{ flex: 1 }}>{label}</Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#999', ml: 1 }}>({count})</Typography>
                                </MenuItem>
                            );
                        })}
                        <Divider sx={{ my: 0.5 }} />
                        {([
                            [4, 'Cancelled'],
                            [5, 'Deleted'],
                        ] as [number, string][]).map(([val, label]) => {
                            const isActive = tabValue === val;
                            const count = getTabCount(val);
                            return (
                                <MenuItem key={val} onClick={() => { setTabValue(val); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75, color: isActive ? '#2D6E6A' : '#999', fontWeight: isActive ? 600 : 400 }}>
                                    <Box sx={{ flex: 1 }}>{label}</Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#bbb', ml: 1 }}>({count})</Typography>
                                </MenuItem>
                            );
                        })}
                        </Collapse>
                        <Divider sx={{ my: 0.5 }} />

                        {/* SECTION: Filter by type */}
                        <MenuItem onClick={() => toggleSection('type')} sx={{ px: 2, py: 0.75, '&:hover': { bgcolor: '#f5f5f5' } }}>
                            <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by type</Typography>
                            {expanded.type ? <ExpandLess sx={{ fontSize: 18, color: '#888' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#888' }} />}
                        </MenuItem>
                        <Collapse in={expanded.type} timeout="auto" unmountOnExit>
                        {(([
                            ['all', 'All Types', Flight, '#666'],
                            ['individual', 'Individual', Person, '#2D6E6A'],
                            ...(groupTripsEnabled ? [['group', 'Group', Group, '#1976D2']] : []),
                        ]) as [TripTypeFilter, string, any, string][]).map(([val, label, IconComp, color]) => {
                            const isActive = tripTypeFilter === val;
                            return (
                                <MenuItem key={val} onClick={() => { setTripTypeFilter(val); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75, fontWeight: isActive ? 600 : 400, color: isActive ? '#2D6E6A' : 'inherit' }}>
                                    <IconComp sx={{ fontSize: 16, color, mr: 1 }} />
                                    <Box sx={{ flex: 1 }}>{label}</Box>
                                </MenuItem>
                            );
                        })}
                        </Collapse>
                        <Divider sx={{ my: 0.5 }} />

                        {/* SECTION: Filter by source */}
                        <MenuItem onClick={() => toggleSection('source')} sx={{ px: 2, py: 0.75, '&:hover': { bgcolor: '#f5f5f5' } }}>
                            <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by source</Typography>
                            {expanded.source ? <ExpandLess sx={{ fontSize: 18, color: '#888' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#888' }} />}
                        </MenuItem>
                        <Collapse in={expanded.source} timeout="auto" unmountOnExit>
                        {([
                            ['all', 'All Sources', Flight, '#666'],
                            ['percoro', 'Percoro', TravelExploreIcon, '#2D6E6A'],
                            ['community', 'Community', Explore, '#e65100'],
                            ['destination', 'Destination', Place, '#3D5A8A'],
                            ...(bookingEnabled ? [['self_imported', 'Self Imported', CloudUpload, '#C2185B'] as [TripSourceFilter, string, any, string]] : []),
                        ] as [TripSourceFilter, string, any, string][]).map(([val, label, IconComp, color]) => {
                            const isActive = tripSourceFilter === val;
                            return (
                                <MenuItem key={val} onClick={() => { setTripSourceFilter(val); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75, fontWeight: isActive ? 600 : 400, color: isActive ? '#2D6E6A' : 'inherit' }}>
                                    <IconComp sx={{ fontSize: 16, color, mr: 1 }} />
                                    <Box sx={{ flex: 1 }}>{label}</Box>
                                </MenuItem>
                            );
                        })}
                        </Collapse>
                        <Divider sx={{ my: 0.5 }} />

                        {/* SECTION: Filter by sharing */}
                        <MenuItem onClick={() => toggleSection('source')} sx={{ px: 2, py: 0.75, '&:hover': { bgcolor: '#f5f5f5' } }}>
                            <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by sharing</Typography>
                            {expanded.source ? <ExpandLess sx={{ fontSize: 18, color: '#888' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#888' }} />}
                        </MenuItem>
                        <Collapse in={expanded.source} timeout="auto" unmountOnExit>
                        {([
                            ['all', 'All trips'],
                            ['shared', 'Trips I\u2019ve shared'],
                            ['companions', 'Trips with Travel Companions'],
                            ['view_only', 'Trips shared view-only'],
                        ] as ['all' | 'shared' | 'companions' | 'view_only', string][]).map(([val, label]) => {
                            const isActive = sharingFilter === val;
                            return (
                                <MenuItem key={val} onClick={() => { setSharingFilter(val); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75, fontWeight: isActive ? 600 : 400, color: isActive ? '#2D6E6A' : 'inherit' }}>
                                    <People sx={{ fontSize: 16, color: '#2D6E6A', mr: 1 }} />
                                    <Box sx={{ flex: 1 }}>{label}</Box>
                                </MenuItem>
                            );
                        })}
                        </Collapse>
                        <Divider sx={{ my: 0.5 }} />

                        {/* SECTION: Export */}
                        <MenuItem onClick={() => toggleSection('exportSection')} sx={{ px: 2, py: 0.75, '&:hover': { bgcolor: '#f5f5f5' } }}>
                            <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Export</Typography>
                            {expanded.exportSection ? <ExpandLess sx={{ fontSize: 18, color: '#888' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#888' }} />}
                        </MenuItem>
                        <Collapse in={expanded.exportSection} timeout="auto" unmountOnExit>
                        <MenuItem onClick={() => { doExportPrint(filteredTrips); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75 }}>
                            <Print sx={{ fontSize: 16, mr: 1, color: '#555' }} />
                            Print all trips
                        </MenuItem>
                        <MenuItem onClick={() => { doExportPDF(filteredTrips); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75 }}>
                            <Download sx={{ fontSize: 16, mr: 1, color: '#555' }} />
                            Download all as PDF
                        </MenuItem>
                        <MenuItem onClick={() => { doExportCSV(filteredTrips); setViewMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', py: 0.75 }}>
                            <TableChart sx={{ fontSize: 16, mr: 1, color: '#555' }} />
                            Export as CSV
                        </MenuItem>
                        </Collapse>
                        <Divider sx={{ my: 0.5 }} />

                        {/* SECTION: Legend (static reference) */}
                        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {Object.entries(typeStyles).filter(([k]) => k !== 'group' || groupTripsEnabled).map(([k, s]) => (
                                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 12, height: 3, borderRadius: '2px', bgcolor: s.border }} />
                                    <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>{s.label}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Menu>

                    {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                    {pendingInvitations.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            {pendingInvitations.map((inv) => (
                                <Box key={inv.companionId} sx={{
                                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5,
                                    p: 2, mb: 1.5, borderRadius: '12px',
                                    bgcolor: 'rgba(0,180,160,0.06)',
                                    border: '1px solid rgba(0,180,160,0.25)',
                                }}>
                                    <People sx={{ fontSize: 20, color: '#2D6E6A', flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#1a1a1a', fontFamily: 'Poppins, sans-serif' }}>
                                            {inv.ownerName} {['completed','cancelled'].includes(inv.status) ? 'shared a trip with you' : 'invited you to join'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.78rem', color: '#555', fontFamily: 'Inter, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {inv.title} · {inv.destination}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                        <Button size="small" variant="contained" disabled={respondingId === inv.companionId}
                                            onClick={() => respondToInvitation(inv.tripId, inv.companionId, 'accept')}
                                            sx={{ bgcolor: '#2D6E6A', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 2, '&:hover': { bgcolor: '#009688' } }}>
                                            {respondingId === inv.companionId ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Accept'}
                                        </Button>
                                        <Button size="small" variant="outlined" disabled={respondingId === inv.companionId}
                                            onClick={() => respondToInvitation(inv.tripId, inv.companionId, 'decline')}
                                            sx={{ borderColor: '#ccc', color: '#666', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 2, '&:hover': { borderColor: '#f44336', color: '#f44336' } }}>
                                            Decline
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Search Bar */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, py: 1, mb: 2,
                        borderRadius: '28px', bgcolor: '#f5f5f5', border: '1px solid #eee',
                        transition: 'all 0.2s',
                        '&:focus-within': { bgcolor: '#fff', border: '1px solid #2D6E6A', boxShadow: '0 0 0 3px rgba(0,180,160,0.08)' },
                    }}>
                        <Search sx={{ fontSize: 20, color: '#aaa' }} />
                        <InputBase
                            placeholder="Search by trip name, destination, trip ID, or reservation code..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ flex: 1, fontSize: '0.88rem', fontFamily: 'Inter, sans-serif', '& input::placeholder': { color: '#bbb', opacity: 1 } }}
                        />
                        {searchQuery && (
                            <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: '#aaa' }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}>X</Typography>
                            </IconButton>
                        )}
                    </Box>

                    {/* Card Grid */}
                    {filteredTrips.length === 0 && !showImportCard ? (
                        <Box sx={{ py: 8, textAlign: 'center', borderRadius: 3, bgcolor: '#fafafa', border: '1px dashed #ddd' }}>
                            <Flight sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                            <Typography variant="h6" sx={{ color: '#666', mb: 1, fontWeight: 500 }}>
                                {trips.length === 0 ? 'No trips yet' : searchQuery ? 'No matching trips' : 'No trips match your filters'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                                {trips.length === 0 ? 'Start planning your next adventure!' : 'Try adjusting your search or filters.'}
                            </Typography>
                            {trips.length === 0 && (
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/trips/new')}
                                        sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#009688' } }}>
                                        Create Your First Trip
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)' },
                            gap: 2,
                        }}>
                            {/* NEW: Import Card - Shows first when active */}
                            {showImportCard && (
                                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                                    <TripImportCard
                                        onImportComplete={handleImportComplete}
                                        onCancel={() => setShowImportCard(false)}
                                    />
                                </Box>
                            )}

                            {filteredTrips.map((trip, index) => {
                                const tk = trip.isGroup ? 'group' : 'individual';
                                const ts = typeStyles[tk];
                                const status = getStatus(trip);
                                const vis = getVisInfo(trip.visibility);
                                const TIcon = ts.icon;
                                const isSel = selectedRows.has(trip.tripId);
                                const isImported = trip.importSource === 'pdf_import' || trip.importSource === 'manual';
                                const isCompanion = (trip as any).isCompanion === true;
                                const isBookable = !isImported && !isCompanion && status.priority <= 2 && status.label !== 'Booked';

                                return (
                                    <React.Fragment key={trip.tripId}>
                                        <Box
                                            onClick={() => navigate(`/trips/${trip.tripId}`)}
                                            sx={{
                                                borderRadius: '16px', cursor: 'pointer', bgcolor: '#fff',
                                                borderLeft: `5px solid ${(trip.tripSource === 'self_imported' || trip.importSource === 'pdf_import') && bookingEnabled ? '#C2185B' : ts.border}`,
                                                boxShadow: isSel
                                                    ? `0 0 0 2px ${ts.border}40, 0 4px 16px ${ts.border}15`
                                                    : '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                                                transition: 'all 0.2s ease', overflow: 'hidden',
                                                display: 'flex', flexDirection: 'column',
                                                '&:hover': { boxShadow: `0 6px 20px ${ts.border}20`, transform: 'translateY(-2px)' },
                                            }}
                                        >
                                            {/* Card Top */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.5 }}>
                                                <Checkbox size="small" checked={isSel}
                                                    onChange={(e) => { e.stopPropagation(); toggleRow(trip.tripId); }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ p: 0, color: '#ddd', '&.Mui-checked': { color: '#2D6E6A' } }} />
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip label={status.label} size="small" color={status.color}
                                                        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }} />
                                                    <IconButton size="small" onClick={(e) => menuOpen(e, trip)}
                                                        sx={{ color: '#ccc', '&:hover': { color: '#666' } }}>
                                                        <MoreVert sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            {/* Card Body */}
                                            <Box sx={{ px: 2, pb: 1.5, flex: 1 }}>
                                                <Typography sx={{
                                                    fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#1a1a1a',
                                                    mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {trip.title}
                                                </Typography>

                                                {isCompanion && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                                        <Box sx={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 0.35,
                                                            px: 0.75, py: 0.2, borderRadius: '6px',
                                                            bgcolor: 'rgba(0,180,160,0.1)', border: '1px solid rgba(0,180,160,0.3)',
                                                        }}>
                                                            <People sx={{ fontSize: 10, color: '#2D6E6A' }} />
                                                            {(trip as any).companionType === 'travel_companion' ? (
                                                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#2D6E6A' }}>
                                                                    Travel Companion{(trip as any).ownerName ? ` of ${(trip as any).ownerName}` : ''}
                                                                </Typography>
                                                            ) : (
                                                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#2D6E6A' }}>
                                                                    Shared with me{(trip as any).ownerName ? ` by ${(trip as any).ownerName}` : ''}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                                    <LocationOn sx={{ fontSize: 15, color: '#aaa' }} />
                                                    <Typography sx={{
                                                        fontSize: '0.82rem', color: '#666', fontWeight: 500, fontFamily: 'Inter, sans-serif',
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                    }}>
                                                        {trip.destination}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.25, color: '#888' }}>
                                                    <CalendarToday sx={{ fontSize: 13 }} />
                                                    <Typography sx={{ fontSize: '0.78rem', fontFamily: 'Inter, sans-serif' }}>
                                                        {formatDateRange(trip.startDate, trip.endDate, { month: 'short', day: 'numeric' })}
                                                    </Typography>
                                                </Box>

                                                {/* Tags */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                    {groupTripsEnabled && (
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.35,
                                                        px: 0.75, py: 0.2, borderRadius: '6px', bgcolor: `${ts.border}10`,
                                                    }}>
                                                        <TIcon sx={{ fontSize: 12, color: ts.border }} />
                                                        <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, color: ts.border }}>{ts.label}</Typography>
                                                    </Box>
                                                    )}
                                                    {trip.isDiscovery && (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.3,
        px: 0.6, py: 0.2, borderRadius: '6px',
        bgcolor: sourceStyles.community.bg, border: `1px solid ${sourceStyles.community.border}`,
    }}>
        <Explore sx={{ fontSize: 10, color: sourceStyles.community.color }} />
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: sourceStyles.community.color }}>Community</Typography>
    </Box>
)}
                                                   {trip.isDestination && (
                                                        <Box sx={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                                            px: 0.6, py: 0.2, borderRadius: '6px',
                                                            bgcolor: sourceStyles.destination.bg, border: `1px solid ${sourceStyles.destination.border}`,
                                                        }}>
                                                            <Place sx={{ fontSize: 10, color: sourceStyles.destination.color }} />
                                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: sourceStyles.destination.color }}>Destination</Typography>
                                                        </Box>
                                                    )}
                                                    {isImported && bookingEnabled && (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.3,
        px: 0.6, py: 0.2, borderRadius: '6px',
        bgcolor: sourceStyles.self_imported.bg, border: `1px solid ${sourceStyles.self_imported.border}`,
    }}>
        <CloudUpload sx={{ fontSize: 10, color: sourceStyles.self_imported.color }} />
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: sourceStyles.self_imported.color }}>Self Imported</Typography>
    </Box>
)}
                                                    <Box sx={{ flex: 1 }} />
                                                    <Tooltip title={vis.label}>
                                                        <Box sx={{ color: '#ccc', display: 'flex', alignItems: 'center' }}>{vis.icon}</Box>
                                                    </Tooltip>
                                                    {!isCompanion && (trip as any).sharingPanel && (trip as any).sharingPanel.length > 0 && (
                                                        <Tooltip title={`Shared with ${(trip as any).sharingPanel.length} people`}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#2D6E6A' }}>
                                                                <People sx={{ fontSize: 13 }} />
                                                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#2D6E6A' }}>
                                                                    {(trip as any).sharingPanel.length}
                                                                </Typography>
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                </Box>

                                                {trip.reservationCode && (
                                                    <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid #f0f0f0' }}>
                                                        <Typography sx={{ fontSize: '0.68rem', color: '#aaa', fontFamily: 'Inter, sans-serif' }}>
                                                            Res: <span style={{ color: '#777', fontWeight: 600 }}>{trip.reservationCode}</span>
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {!isCompanion && (trip as any).sharingPanel && (trip as any).sharingPanel.length > 0 && (() => {
                                                    const panel = (trip as any).sharingPanel as any[];
                                                    const companions = panel.filter((p: any) => p.companionType === 'travel_companion').length;
                                                    const shared = panel.filter((p: any) => p.companionType !== 'travel_companion').length;
                                                    return (
                                                        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid #f0f0f0' }}>
                                                            <Typography sx={{ fontSize: '0.65rem', color: '#aaa', fontFamily: 'Inter, sans-serif' }}>
                                                                {shared > 0 && `${shared} shared`}
                                                                {shared > 0 && companions > 0 && ' · '}
                                                                {companions > 0 && `${companions} companion${companions !== 1 ? 's' : ''}`}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })()}
                                            </Box>

                                            {/* Upload CTA — only visible on bookable (active/upcoming) trips */}
                                            {isBookable && (
                                                <Box sx={{ px: 2, pb: 1, pt: 0.5, borderTop: '1px solid #f5f5f5' }}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/trips/${trip.tripId}?openUpload=1`); }}
                                                        sx={{
                                                            borderColor: '#2D6E6A',
                                                            color: '#2D6E6A',
                                                            borderRadius: '10px',
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.78rem',
                                                            py: 0.7,
                                                            fontFamily: 'Poppins, sans-serif',
                                                            '&:hover': { borderColor: '#009688', bgcolor: '#E8F5F3' },
                                                        }}
                                                    >
                                                        Upload Booking
                                                    </Button>
                                                </Box>
                                            )}
                                            {/* Book Button */}
                                            {isBookable && showBookOnMyTrips && (
                                                <Box sx={{ px: 2, pb: 1.5, pt: 0.5, borderTop: '1px solid #f5f5f5' }}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        startIcon={<ShoppingCartCheckout sx={{ fontSize: 16 }} />}
                                                        onClick={(e) => handleBookClick(e, trip)}
                                                        sx={{
                                                            bgcolor: '#FF6B35',
                                                            borderRadius: '10px',
                                                            textTransform: 'none',
                                                            fontWeight: 700,
                                                            fontSize: '0.82rem',
                                                            py: 0.8,
                                                            fontFamily: 'Poppins, sans-serif',
                                                            boxShadow: '0 2px 8px rgba(255,107,53,0.3)',
                                                            '&:hover': {
                                                                bgcolor: '#E55A2B',
                                                                boxShadow: '0 4px 12px rgba(255,107,53,0.4)',
                                                            },
                                                        }}
                                                    >
                                                        Book This Trip
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Mobile In-Feed Ad: After every 6 cards */}
                                        {isMobile && (index + 1) % 6 === 0 && index !== filteredTrips.length - 1 && (
                                            <Box sx={{ gridColumn: '1 / -1' }}>
                                                <InFeedAd page="my_trips" index={Math.floor(index / 6)} variant="compact" />
                                            </Box>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    )}

                    {/* Footer */}
                    {filteredTrips.length > 0 && (
                        <Box sx={{ mt: 2, px: 1 }}>
                            <Typography variant="caption" sx={{ color: '#bbb' }}>
                                {filteredTrips.length} {filteredTrips.length === 1 ? 'trip' : 'trips'}
                                {selectedRows.size > 0 && ` - ${selectedRows.size} selected`}
                            </Typography>
                        </Box>
                    )}
                </Grid>

                {/* Sidebar Ad - Desktop Only */}
                {!isMobile && (
                    <Grid item md={3}>
                        <SidebarAd page="my_trips" />
                    </Grid>
                )}
            </Grid>

            <Snackbar open={!!inviteSnackbar} autoHideDuration={4000} onClose={() => setInviteSnackbar(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setInviteSnackbar(null)} severity="success" sx={{ width: '100%' }}>{inviteSnackbar}</Alert>
            </Snackbar>

            {/* Coming Soon Snackbar */}
            <Snackbar
                open={!!comingSoonMessage}
                autoHideDuration={4000}
                onClose={() => setComingSoonMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setComingSoonMessage('')}
                    severity="info"
                    sx={{ width: '100%' }}
                >
                    {comingSoonMessage}
                </Alert>
            </Snackbar>

            {/* Booking Prompt Dialog */}
            <Dialog
                open={bookingPromptOpen}
                onClose={() => setBookingPromptOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
            >
                <Box sx={{ height: 6, bgcolor: '#FF6B35' }} />
                <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', pb: 1, pt: 2.5 }}>
                    Book Your Trip
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    {bookingTrip && (
                        <Box sx={{ mb: 2.5 }}>
                            <Box sx={{
                                p: 2, borderRadius: 3, bgcolor: '#f8f9fa',
                                border: '1px solid #eee', mb: 2.5,
                            }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5, fontFamily: 'Poppins, sans-serif' }}>
                                    {bookingTrip.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, color: '#666' }}>
                                    <LocationOn sx={{ fontSize: 14 }} />
                                    <Typography sx={{ fontSize: '0.82rem' }}>{bookingTrip.destination}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#888' }}>
                                    <CalendarToday sx={{ fontSize: 13 }} />
                                    <Typography sx={{ fontSize: '0.78rem' }}>{formatDateRange(bookingTrip.startDate, bookingTrip.endDate, { month: 'short', day: 'numeric' })}</Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ fontSize: '0.88rem', color: '#555', mb: 2, lineHeight: 1.6 }}>
                                How would you like to proceed?
                            </Typography>

                            <Box
                                onClick={handleReviewThenBook}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 2,
                                    p: 2, borderRadius: 3, cursor: 'pointer',
                                    border: '1px solid #e0e0e0',
                                    mb: 1.5,
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#2D6E6A', bgcolor: '#f5fdfb', boxShadow: '0 2px 8px rgba(0,180,160,0.1)' },
                                }}
                            >
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: '12px',
                                    bgcolor: '#e8f5f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <Info sx={{ color: '#2D6E6A', fontSize: 22 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a1a', mb: 0.25 }}>
                                        Review Trip Details
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#888', lineHeight: 1.4 }}>
                                        Review itinerary, select hotels, flights, and activities before booking
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                onClick={handleQuickBook}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 2,
                                    p: 2, borderRadius: 3, cursor: 'pointer',
                                    border: '1px solid #e0e0e0',
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#FF6B35', bgcolor: '#fff8f5', boxShadow: '0 2px 8px rgba(255,107,53,0.1)' },
                                }}
                            >
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: '12px',
                                    bgcolor: '#fff0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <ShoppingCartCheckout sx={{ color: '#FF6B35', fontSize: 22 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a1a', mb: 0.25 }}>
                                        Check Availability & Rates
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#888', lineHeight: 1.4 }}>
                                        Validate availability, view current rates, and proceed to payment
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
                    <Button onClick={() => setBookingPromptOpen(false)}
                        sx={{ textTransform: 'none', borderRadius: 2, color: '#999' }}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Menus & Dialogs */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={menuClose}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 170, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
                <MenuItem onClick={viewTrip} sx={{ fontSize: '0.85rem', gap: 1.5 }}><Visibility sx={{ fontSize: 18 }} /> View Details</MenuItem>
                {!(selectedTrip as any)?.isCompanion && (
                    <MenuItem onClick={editTrip} sx={{ fontSize: '0.85rem', gap: 1.5 }}><Edit sx={{ fontSize: 18 }} /> Edit</MenuItem>
                )}
                {showBookOnMyTrips && (
                    <MenuItem onClick={bookFromMenu} sx={{ fontSize: '0.85rem', gap: 1.5, color: '#FF6B35' }}>
                        <ShoppingCartCheckout sx={{ fontSize: 18 }} /> Book Trip
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={printTrip} sx={{ fontSize: '0.85rem', gap: 1.5 }}><Print sx={{ fontSize: 18 }} /> Print</MenuItem>
                <MenuItem onClick={downloadPDF} sx={{ fontSize: '0.85rem', gap: 1.5 }}><Download sx={{ fontSize: 18 }} /> Download PDF</MenuItem>
                <Divider />
                {!(selectedTrip as any)?.isCompanion && (
                    <MenuItem onClick={deleteClick} sx={{ fontSize: '0.85rem', gap: 1.5, color: 'error.main' }}><Delete sx={{ fontSize: 18 }} /> Delete</MenuItem>
                )}
            </Menu>

            <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>Delete Trip?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete "{selectedTrip?.title}"? This action cannot be undone.</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
                    <Button onClick={deleteConfirm} disabled={deleting} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: 2 }}>
                        {deleting ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyTripsPage;